import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, Activity, Mail, Lock, Eye, EyeOff, User, Phone, MapPin, Clock, FileText, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { isValidPhone } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = [
  { id: 'general', name_fr: 'Médecine générale' },
  { id: 'cardiology', name_fr: 'Cardiologie' },
  { id: 'dermatology', name_fr: 'Dermatologie' },
  { id: 'gynecology', name_fr: 'Gynécologie' },
  { id: 'ophthalmology', name_fr: 'Ophtalmologie' },
  { id: 'pediatrics', name_fr: 'Pédiatrie' },
  { id: 'dentistry', name_fr: 'Dentiste' },
  { id: 'orthopedics', name_fr: 'Orthopédie' },
  { id: 'psychiatry', name_fr: 'Psychiatrie' },
  { id: 'neurology', name_fr: 'Neurologie' },
  { id: 'urology', name_fr: 'Urologie' },
  { id: 'ent', name_fr: 'ORL' },
  { id: 'radiology', name_fr: 'Radiologie' },
  { id: 'gastroenterology', name_fr: 'Gastro-entérologie' },
  { id: 'pneumology', name_fr: 'Pneumologie' },
];

const WILAYAS = [
  { code: '01', name_fr: 'Adrar' }, { code: '02', name_fr: 'Chlef' }, { code: '03', name_fr: 'Laghouat' },
  { code: '04', name_fr: 'Oum El Bouaghi' }, { code: '05', name_fr: 'Batna' }, { code: '06', name_fr: 'Béjaïa' },
  { code: '07', name_fr: 'Biskra' }, { code: '08', name_fr: 'Béchar' }, { code: '09', name_fr: 'Blida' },
  { code: '10', name_fr: 'Bouira' }, { code: '11', name_fr: 'Tamanrasset' }, { code: '12', name_fr: 'Tébessa' },
  { code: '13', name_fr: 'Tlemcen' }, { code: '14', name_fr: 'Tiaret' }, { code: '15', name_fr: 'Tizi Ouzou' },
  { code: '16', name_fr: 'Alger' }, { code: '17', name_fr: 'Djelfa' }, { code: '18', name_fr: 'Jijel' },
  { code: '19', name_fr: 'Sétif' }, { code: '20', name_fr: 'Saïda' }, { code: '21', name_fr: 'Skikda' },
  { code: '22', name_fr: 'Sidi Bel Abbès' }, { code: '23', name_fr: 'Annaba' }, { code: '24', name_fr: 'Guelma' },
  { code: '25', name_fr: 'Constantine' }, { code: '26', name_fr: 'Médéa' }, { code: '27', name_fr: 'Mostaganem' },
  { code: '28', name_fr: "M'Sila" }, { code: '29', name_fr: 'Mascara' }, { code: '30', name_fr: 'Ouargla' },
  { code: '31', name_fr: 'Oran' }, { code: '32', name_fr: 'El Bayadh' }, { code: '33', name_fr: 'Illizi' },
  { code: '34', name_fr: 'Bordj Bou Arréridj' }, { code: '35', name_fr: 'Boumerdès' }, { code: '36', name_fr: 'El Tarf' },
  { code: '37', name_fr: 'Tindouf' }, { code: '38', name_fr: 'Tissemsilt' }, { code: '39', name_fr: 'El Oued' },
  { code: '40', name_fr: 'Khenchela' }, { code: '41', name_fr: 'Souk Ahras' }, { code: '42', name_fr: 'Tipaza' },
  { code: '43', name_fr: 'Mila' }, { code: '44', name_fr: 'Aïn Defla' }, { code: '45', name_fr: 'Naâma' },
  { code: '46', name_fr: 'Aïn Témouchent' }, { code: '47', name_fr: 'Ghardaïa' }, { code: '48', name_fr: 'Relizane' },
];

// ✅ Compresse et redimensionne l'image en base64
// Correctif : ni reader.onerror ni img.onerror n'étaient gérés — un fichier
// illisible ou une image corrompue ne faisait ni résoudre ni rejeter cette
// Promise, qui restait alors indéfiniment en attente. handlePhotoChange
// (await compressImage(...)) restait donc bloqué sur "Chargement..." pour
// toujours, sans message d'erreur ni possibilité de réessayer.
const compressImage = (file, maxSize = 400) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Image invalide ou corrompue'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
        } else {
          if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const DoctorRegister = () => {
  const navigate = useNavigate();
  const { registerDoctor } = useAuth();
  const [step, setStep] = useState(1);
  const photoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    specialty: '',
    wilaya_code: '',
    address: '',
    fees: '',
    license_number: '',
    photo_url: '',
    // Correctif : un planning hebdomadaire complet était auparavant imposé
    // par défaut, sans que le médecin ne le voie ni ne le choisisse — il
    // devenait réservable par des patients sur des horaires qu'il n'avait
    // jamais validés. Le planning part maintenant vide ; le médecin le
    // configure lui-même depuis l'onglet "Planning" de son tableau de bord
    // (aucun patient ne peut réserver tant qu'aucun créneau n'y est ouvert).
    schedule: {}
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [error, setError] = useState('');

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  // ✅ Gestion upload photo
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Veuillez choisir un fichier image (JPG, PNG...)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La photo ne doit pas dépasser 5 MB');
      return;
    }

    setPhotoLoading(true);
    try {
      const compressed = await compressImage(file, 400);
      setFormData((prev) => ({ ...prev, photo_url: compressed }));
      toast.success('Photo ajoutée !');
    } catch (err) {
      toast.error('Erreur lors du chargement de la photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password) {
      setError('Veuillez remplir tous les champs');
      return false;
    }
    // Correctif : un prenom et un nom identiques ("Yacine Yacine") passaient
    // sans etre detectes.
    if (formData.firstName.trim().toLowerCase() === formData.lastName.trim().toLowerCase()) {
      setError('Le prénom et le nom ne peuvent pas être identiques');
      return false;
    }
    // Correctif : aucun format n'était vérifié côté client — une adresse
    // sans "@" ou un téléphone d'un seul caractère passait cette étape et
    // n'échouait qu'au moment de la soumission finale (ou pas du tout, le
    // serveur ne validant pas non plus ces formats).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError('Adresse email invalide');
      return false;
    }
    // Correctif : un simple comptage de chiffres (9-13) laissait passer des
    // numeros algeriens incomplets (ex: "561250610", 0 initial manquant, ou
    // "+21356170681", un chiffre manquant apres +213) qui tombaient par
    // coincidence dans cette plage - constate sur des comptes reels. Regle
    // partagee avec l'inscription patient (lib/utils.js) qui connait le
    // format algerien local/indicatif plutot qu'un simple comptage.
    if (!isValidPhone(formData.phone)) {
      setError('Numéro de téléphone invalide');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setError('Le mot de passe doit contenir au moins une majuscule');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setError('Le mot de passe doit contenir au moins une minuscule');
      return false;
    }
    if (!/[0-9]/.test(formData.password)) {
      setError('Le mot de passe doit contenir au moins un chiffre');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    if (!formData.specialty || !formData.wilaya_code || !formData.address || !formData.fees || !formData.license_number) {
      setError('Veuillez remplir tous les champs');
      return false;
    }
    // Correctif : "fees" et "license_number" n'étaient vérifiés que sur leur
    // présence, jamais sur leur contenu (un "0", un nombre négatif, ou un
    // numéro de licence d'un seul caractère passaient sans problème).
    if (!Number.isFinite(parseInt(formData.fees, 10)) || parseInt(formData.fees, 10) <= 0) {
      setError('Les honoraires doivent être un nombre positif');
      return false;
    }
    if (formData.license_number.trim().length < 3) {
      setError('Numéro de licence invalide');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setLoading(true);
    setError('');

    try {
      await registerDoctor({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        specialty: formData.specialty,
        wilaya_code: formData.wilaya_code,
        address: formData.address,
        fees: parseInt(formData.fees),
        license_number: formData.license_number,
        photo_url: formData.photo_url || null,
        schedule: formData.schedule
      });
      toast.success("Inscription soumise ! En attente d'approbation.");
      // Le planning part vide (voir formData.schedule ci-dessus) : on le
      // rappelle explicitement, sinon rien n'indique au médecin qu'il doit
      // encore configurer ses créneaux avant d'être réservable.
      setTimeout(() => toast.info('Pensez à configurer votre planning dans l\'onglet "Planning" pour devenir réservable par les patients.'), 1200);
      navigate('/doctor/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="flex items-center text-[#0596DE]">
            <Moon className="w-8 h-8" />
            <Activity className="w-8 h-8 -ml-2" />
          </div>
          <span className="font-bold text-2xl text-[#0596DE] font-['Outfit']">Tabbibak.dz</span>
        </Link>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8">
          <h1 className="text-2xl font-bold text-[#0F172A] font-['Outfit'] text-center mb-2">
            Inscription Médecin
          </h1>
          <p className="text-[#64748B] text-center mb-8">
            Créez votre profil pour recevoir des rendez-vous en ligne
          </p>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#0596DE]' : 'text-[#64748B]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#0596DE] text-white' : 'bg-[#F1F5F9]'}`}>
                1
              </div>
              <span className="hidden sm:inline">Informations</span>
            </div>
            <div className="w-8 h-px bg-[#E2E8F0]" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#0596DE]' : 'text-[#64748B]'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#0596DE] text-white' : 'bg-[#F1F5F9]'}`}>
                2
              </div>
              <span className="hidden sm:inline">Profil médical</span>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6" data-testid="doctor-register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="doctor-register-form">
            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">Prénom</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                      <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Votre prénom" className="pl-10 rounded-xl" required data-testid="doctor-first-name-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="lastName">Nom</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                      <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Votre nom" className="pl-10 rounded-xl" required data-testid="doctor-last-name-input" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email professionnel</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="votre@email.com" className="pl-10 rounded-xl" required data-testid="doctor-email-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone du cabinet</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+213 5XX XX XX XX" className="pl-10 rounded-xl" required data-testid="doctor-phone-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} placeholder="••••••••" className="pl-10 pr-10 rounded-xl" required data-testid="doctor-password-input" />
                    <p className="text-xs text-[#64748B] mt-1">Min. 8 caractères, avec 1 majuscule, 1 minuscule et 1 chiffre</p>
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B]">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="confirmPassword" name="confirmPassword" type={showPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••" className="pl-10 rounded-xl" required data-testid="doctor-confirm-password-input" />
                  </div>
                </div>

                <Button type="button" onClick={() => validateStep1() && setStep(2)} className="w-full rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12" data-testid="doctor-next-step-btn">
                  Continuer
                </Button>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-5">

                {/* ✅ Upload photo */}
                <div>
                  <Label>Photo de profil (optionnel)</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {/* Aperçu */}
                    <div className="w-20 h-20 rounded-xl bg-[#E6F4FB] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {formData.photo_url ? (
                        <img src={formData.photo_url} alt="Aperçu" className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-8 h-8 text-[#0596DE]" />
                      )}
                    </div>
                    {/* Bouton */}
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={photoInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={photoLoading}
                        className="w-full rounded-xl border-dashed border-2 border-[#0596DE] text-[#0596DE] hover:bg-[#E6F4FB]"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        {photoLoading ? 'Chargement...' : formData.photo_url ? 'Changer la photo' : 'Ajouter une photo'}
                      </Button>
                      <p className="text-xs text-[#64748B] mt-1">JPG, PNG — max 5 MB</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Spécialité</Label>
                  <Select value={formData.specialty} onValueChange={(v) => handleSelectChange('specialty', v)}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="doctor-specialty-select">
                      <SelectValue placeholder="Sélectionnez votre spécialité" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPECIALTIES.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name_fr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Wilaya</Label>
                  <Select value={formData.wilaya_code} onValueChange={(v) => handleSelectChange('wilaya_code', v)}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="doctor-wilaya-select">
                      <SelectValue placeholder="Sélectionnez votre wilaya" />
                    </SelectTrigger>
                    <SelectContent>
                      {WILAYAS.map(w => (
                        <SelectItem key={w.code} value={w.code}>{w.code} - {w.name_fr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="address">Adresse du cabinet</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="address" name="address" value={formData.address} onChange={handleChange} placeholder="123 Rue Exemple, Ville" className="pl-10 rounded-xl" required data-testid="doctor-address-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="fees">Honoraires de consultation (DZD)</Label>
                  <div className="relative mt-1">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="fees" name="fees" type="number" value={formData.fees} onChange={handleChange} placeholder="2000" className="pl-10 rounded-xl" required data-testid="doctor-fees-input" />
                  </div>
                  <p className="text-xs text-[#64748B] mt-1">Cette information restera privée et ne sera pas affichée sur votre profil public.</p>
                </div>

                <div>
                  <Label htmlFor="license_number">Numéro de licence / INAMI</Label>
                  <div className="relative mt-1">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input id="license_number" name="license_number" value={formData.license_number} onChange={handleChange} placeholder="INAMI-XX-XXXX-XXX" className="pl-10 rounded-xl" required data-testid="doctor-license-input" />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full h-12">
                    Retour
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12" data-testid="doctor-submit-btn">
                    {loading ? 'Inscription...' : "S'inscrire"}
                  </Button>
                </div>
              </div>
            )}
          </form>

          <p className="text-center text-[#64748B] mt-8">
            Déjà inscrit ?{' '}
            <Link to="/login" className="text-[#0596DE] hover:underline font-medium">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DoctorRegister;